import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'shared/hooks';

import './styles.scss'
import { ICONS } from 'shared/constants';
import { UiIcon } from 'shared/uikit';

type UiHintPosition = "top" | "bottom" | "left" | "right";

interface UiHintProps {
    text: string;
    position?: UiHintPosition;
    delay?: number;
    className?: string;
    iconClassName?: string;
    tooltipClassName?: string;
    maxWidth?: number | string;
    disabled?: boolean;
}

export const UiHint = observer(({
    text,
    position = 'top',
    delay = 200,
    className = '',
    iconClassName = '',
    tooltipClassName = '',
    maxWidth = 200,
    disabled = false
}: UiHintProps) => {

    const store = useStore(() => ({
        isVisible: false,
        activePosition: position,
        alignmentOffset: 0,
    }));

    const wrapperRef = useRef<HTMLDivElement>(null);
    const iconRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const showHint = () => {
        if (disabled) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            store.set("isVisible", true);
        }, delay);
    };

    const hideHint = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        store.set("isVisible", false);
    };

    const calculateAlignment = (iconRect: DOMRect, tooltipRect: DOMRect, position: UiHintPosition) => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        let offset = 0;

        switch (position) {
            case 'top':
            case 'bottom': {
                const iconCenterX = iconRect.left + (iconRect.width / 2);
                const idealTooltipLeft = iconCenterX - (tooltipRect.width / 2);
                const idealTooltipRight = idealTooltipLeft + tooltipRect.width;

                if (idealTooltipLeft < 0) {
                    const alignToLeftEdge = iconRect.left;
                    offset = alignToLeftEdge - idealTooltipLeft;
                }
                else if (idealTooltipRight > viewportWidth) {
                    const alignToRightEdge = iconRect.right - tooltipRect.width;
                    offset = alignToRightEdge - idealTooltipLeft;
                }
                break;
            }
            case 'left':
            case 'right': {
                const iconCenterY = iconRect.top + (iconRect.height / 2);
                const idealTooltipTop = iconCenterY - (tooltipRect.height / 2);
                const idealTooltipBottom = idealTooltipTop + tooltipRect.height;

                if (idealTooltipTop < 0) {
                    const alignToTopEdge = iconRect.top;
                    offset = alignToTopEdge - idealTooltipTop;
                }
                else if (idealTooltipBottom > viewportHeight) {
                    const alignToBottomEdge = iconRect.bottom - tooltipRect.height;
                    offset = alignToBottomEdge - idealTooltipTop;
                }
                break;
            }
        }

        return offset;
    };

    useEffect(() => {
        if (store.isVisible && iconRef.current && tooltipRef.current) {
            const iconRect = iconRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let newPosition = position;

            switch (position) {
                case 'top':
                    if (iconRect.top - tooltipRect.height < 0) {
                        newPosition = 'bottom';
                    }
                    break;
                case 'bottom':
                    if (iconRect.bottom + tooltipRect.height > viewportHeight) {
                        newPosition = 'top';
                    }
                    break;
                case 'left':
                    if (iconRect.left - tooltipRect.width < 0) {
                        newPosition = 'right';
                    }
                    break;
                case 'right':
                    if (iconRect.right + tooltipRect.width > viewportWidth) {
                        newPosition = 'left';
                    }
                    break;
            }

            store.set("activePosition", newPosition);

            const alignmentOffset = calculateAlignment(iconRect, tooltipRect, newPosition);
            store.set("alignmentOffset", alignmentOffset);
        }
    }, [store.isVisible, position]);

    const tooltipClasses = ['ui-hint__tooltip', `ui-hint__tooltip--${store.activePosition}`, store.isVisible ? 'ui-hint__tooltip--visible' : 'ui-hint__tooltip--hidden', tooltipClassName].filter(Boolean).join(' ');

    const maxWidthStyle = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    const alignmentStyle: React.CSSProperties = {
        maxWidth: maxWidthStyle,
        ...(store.alignmentOffset !== 0 && {
            transform: store.activePosition === 'top' || store.activePosition === 'bottom'
                ? `translateX(calc(-50% + ${store.alignmentOffset}px))`
                : store.activePosition === 'left' || store.activePosition === 'right'
                    ? `translateY(calc(-50% + ${store.alignmentOffset}px))`
                    : undefined
        })
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={wrapperRef}
            className={`ui-hint ${className}`}
            onMouseEnter={showHint}
            onMouseLeave={hideHint}
        >
            <div
                ref={iconRef}
                className={`ui-hint__icon ${iconClassName}`}
            >
                <UiIcon icon={ICONS.INFO_CIRCLE} size={16} color="#15171C" />
            </div>
            <div
                ref={tooltipRef}
                className={tooltipClasses}
                style={alignmentStyle}
            >
                <div className="ui-hint__content">
                    {text}
                </div>
            </div>
        </div>
    );
});
